import { Box, Button, Card, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { ConstructionAdderType, PriceSheetMappingBulkChangesPayload, PricingSettingsType, ProgramType } from 'types';

import { api } from '../../../../services/api';
import {
  useEnqueuePriceSheetMappingBulkJobMutation,
  useGetConstructionAddersQuery,
  useGetHoldbacksQuery,
  useGetItcModifiersQuery,
  useGetModifiersQuery,
  useGetPriceSheetMappingBulkJobQuery,
  useGetPriceSheetsQuery,
} from '../../../../services/pricing';
import { FilteredMappingsList } from '../FilteredMappingsList';
import '../mappings.css';

import { getColumnConfig } from './BulkChangesColumnConfig';
import { getConsumerSolarColumnConfig } from './BulkEditConsumerSolarColumnConfig';
import { getEnergyBackupColumnConfig } from './BulkEditEnergyBackupColumnConfig';
import { BulkEditModal } from './BulkEditModal';

export const BulkEditMappings = () => {
  const [currentEditColumn, setCurrentEditColumn] = useState<string | null>(null);
  const { programType } = useParams<{ programType: string }>();
  const { data: priceSheetResults } = useGetPriceSheetsQuery({
    pageNum: 1,
    pageSize: 1000,
    programType: programType as ProgramType,
  });
  const { data: energyCommunities = [] } = useGetItcModifiersQuery({
    type: PricingSettingsType.energyCommunity,
    programType: programType as ProgramType,
  });
  const { data: ppwModifiers = [] } = useGetModifiersQuery({
    type: PricingSettingsType.ppwAdjustment,
    programType: programType as ProgramType,
  });
  const { data: domesticContentModifiers = [] } = useGetItcModifiersQuery({
    type: PricingSettingsType.domesticContent,
    programType: programType as ProgramType,
  });
  const { data: holdbacks = [] } = useGetHoldbacksQuery({ programType: programType as ProgramType });
  const { data: arbitrageBatteryAdders = [] } = useGetConstructionAddersQuery({
    type: ConstructionAdderType.arbitrageBattery,
    programType: programType as ProgramType,
  });
  const { data: arbitrageBatteryCostAdders = [] } = useGetConstructionAddersQuery({
    type: ConstructionAdderType.arbitrageBatteryCost,
    programType: programType as ProgramType,
  });
  const { data: arbitrageBatteryPriceCapsAdders = [] } = useGetConstructionAddersQuery({
    type: ConstructionAdderType.arbitrageBatteryPriceCap,
    programType: programType as ProgramType,
  });
  const { data: electricalUpgradeAdders = [] } = useGetConstructionAddersQuery({
    type: ConstructionAdderType.electricalUpgrade,
    programType: programType as ProgramType,
  });
  const { data: backupBatteryAdders = [] } = useGetConstructionAddersQuery({
    type: ConstructionAdderType.backupBattery,
    programType: programType as ProgramType,
  });
  const { data: backupBatteryKwhAdders = [] } = useGetConstructionAddersQuery({
    type: ConstructionAdderType.backupBatteryKwh,
    programType: programType as ProgramType,
  });
  const { data: backupBatteryPriceCaps = [] } = useGetConstructionAddersQuery({
    type: ConstructionAdderType.backupBatteryPriceCap,
    programType: programType as ProgramType,
  });
  const { data: batteryMinimumCapacities = [] } = useGetConstructionAddersQuery({
    type: ConstructionAdderType.batteryMinimumCapacity,
    programType: programType as ProgramType,
  });

  const dispatch = useDispatch();
  const [enqueueBulkJob, { isLoading: isEnqueueing }] = useEnqueuePriceSheetMappingBulkJobMutation();
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const { data: jobStatus } = useGetPriceSheetMappingBulkJobQuery(
    activeJobId ? { jobId: activeJobId } : (undefined as any),
    {
      skip: !activeJobId,
      // SSE drives the cache on the happy path; the polling interval is a
      // fallback so a dropped EventSource (LB timeout, server restart,
      // transient network blip) still resolves the modal once the job
      // finishes server-side. Polling stops as soon as activeJobId clears
      // in the terminal-state effect below.
      pollingInterval: 5000,
    },
  );
  const isJobActive = !!activeJobId && jobStatus?.status !== 'completed' && jobStatus?.status !== 'failed';
  const isLoading = isEnqueueing || isJobActive;

  useEffect(() => {
    if (!jobStatus || !activeJobId) return;
    if (jobStatus.status === 'completed') {
      dispatch(api.util.invalidateTags([{ type: 'PriceSheetMappings', id: 'LIST' }]));
      toast.success(`Bulk price-sheet-mapping update complete (${jobStatus.processed}/${jobStatus.total})`);
      setActiveJobId(null);
    } else if (jobStatus.status === 'failed') {
      toast.error(`Bulk price-sheet-mapping update failed: ${jobStatus.error ?? 'unknown error'}`);
      setActiveJobId(null);
    }
  }, [jobStatus, activeJobId, dispatch]);

  const [searchParams] = useSearchParams();
  const advancedFilters = searchParams.get('advancedFilters');
  const searchTerm = searchParams.get('search') || undefined;

  const [editValuesDisplay, setEditValuesDisplay] = useState<
    Record<string, string | { id: string; name: string | number }>
  >({});
  const [editValues, setEditValuesInternal] = useState<Record<string, string>>({});

  const setEditValues = useCallback(
    (values: Record<string, string>) => {
      const currentValues = { ...editValues, ...values };
      setEditValuesInternal(currentValues);
      const {
        absoluteCeiling,
        escalationRateMax,
        escalationRateMin,
        priceSheet,
        pvOnlyCeiling,
        pvOnlyMin,
        zeroEscalatorCeiling,
        holdback,
        energyCommunity,
        ppwModifier,
        domesticContentModifier,
        arbitrageBattery,
        arbitrageBatteryCost,
        arbitrageBatteryPriceCap,
        electricalUpgrade,
        backupBattery,
        backupBatteryKwh,
        backupBatteryPriceCap,
        batteryMinimumCapacity,
        // ConsumerSolar-specific fields
        minSystemSize,
        maxSystemSize,
        minProductionFactor,
        maxProductionFactor,
        // EnergyBackup-specific fields
        minTerm,
        maxTerm,
        minSystemCost,
        maxSystemCost,
      } = currentValues;
      const newDisplayValues: Record<string, string | { id: string; name: string }> = {
        holdback: holdback ? (holdbacks.find((hb: any) => hb.id === holdback) ?? 'remove') : undefined,
        energyCommunity: energyCommunity
          ? (energyCommunities.find((ec: any) => ec.id === energyCommunity) ?? 'remove')
          : undefined,
        ppwModifier: ppwModifier ? (ppwModifiers.find((ppw: any) => ppw.id === ppwModifier) ?? 'remove') : undefined,
        domesticContentModifier: domesticContentModifier
          ? (domesticContentModifiers.find((dcm: any) => dcm.id === domesticContentModifier) ?? 'remove')
          : undefined,
        arbitrageBattery: arbitrageBattery
          ? (arbitrageBatteryAdders.find((aba: any) => aba.id === arbitrageBattery) ?? 'remove')
          : undefined,
        arbitrageBatteryCost: arbitrageBatteryCost
          ? (arbitrageBatteryCostAdders.find((aba: any) => aba.id === arbitrageBatteryCost) ?? 'remove')
          : undefined,
        arbitrageBatteryPriceCap: arbitrageBatteryPriceCap
          ? (arbitrageBatteryPriceCapsAdders.find((aba: any) => aba.id === arbitrageBatteryPriceCap) ?? 'remove')
          : undefined,
        electricalUpgrade: electricalUpgrade
          ? (electricalUpgradeAdders.find((ela: any) => ela.id === electricalUpgrade) ?? 'remove')
          : undefined,
        backupBattery: backupBattery
          ? (backupBatteryAdders.find((bub: any) => bub.id === backupBattery) ?? 'remove')
          : undefined,
        backupBatteryKwh: backupBatteryKwh
          ? (backupBatteryKwhAdders.find((bub: any) => bub.id === backupBatteryKwh) ?? 'remove')
          : undefined,
        backupBatteryPriceCap: backupBatteryPriceCap
          ? (backupBatteryPriceCaps.find((priceCap: any) => priceCap.id === backupBatteryPriceCap) ?? 'remove')
          : undefined,
        batteryMinimumCapacity: batteryMinimumCapacity
          ? (batteryMinimumCapacities.find((minCap: any) => minCap.id === batteryMinimumCapacity) ?? 'remove')
          : undefined,
        absoluteCeiling,
        escalationRateMax,
        escalationRateMin,
        priceSheet,
        pvOnlyCeiling,
        pvOnlyMin,
        zeroEscalatorCeiling,
        // ConsumerSolar-specific fields
        minSystemSize,
        maxSystemSize,
        minProductionFactor,
        maxProductionFactor,
        // EnergyBackup-specific fields
        minTerm,
        maxTerm,
        minSystemCost,
        maxSystemCost,
      };
      setEditValuesDisplay(newDisplayValues);
    },
    [
      editValues,
      setEditValuesDisplay,
      setEditValuesInternal,
      electricalUpgradeAdders,
      backupBatteryAdders,
      backupBatteryKwhAdders,
      backupBatteryPriceCaps,
      holdbacks,
      energyCommunities,
      ppwModifiers,
      domesticContentModifiers,
      priceSheetResults,
      arbitrageBatteryAdders,
      arbitrageBatteryCostAdders,
      arbitrageBatteryPriceCapsAdders,
      batteryMinimumCapacities,
    ],
  );

  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();

  const onColumnClick = useCallback(
    (dataKey: string) => {
      setCurrentEditColumn(dataKey);
      openModal();
    },
    [openModal],
  );

  const confirmApplyBulkChanges = useCallback(async () => {
    const confirmed = window.confirm(`Selected price mappings will be replaced. \n Are you sure?`);
    if (!confirmed) return;

    const isEnergyBackup = programType === ProgramType.energyBackup;
    const isConsumerSolar = programType === ProgramType.consumerSolar;

    const payload: PriceSheetMappingBulkChangesPayload = {
      filters: {
        advancedFilters,
        searchTerm,
      },
      // Consumer Solar specific price sheet fields
      ...(isConsumerSolar && editValues.priceSheet
        ? {
            priceSheetId: editValues.priceSheet,
            escalationRateMin: parseFloat(editValues.escalationRateMin),
            escalationRateMax: parseFloat(editValues.escalationRateMax),
            minSystemSize: parseFloat(editValues.minSystemSize),
            maxSystemSize: parseFloat(editValues.maxSystemSize),
            minProductionFactor: parseFloat(editValues.minProductionFactor),
            maxProductionFactor: parseFloat(editValues.maxProductionFactor),
          }
        : {}),
      // Solar/NewHomes specific price sheet fields
      ...(!isEnergyBackup && !isConsumerSolar && editValues.priceSheet
        ? {
            priceSheetId: editValues.priceSheet,
            pvOnlyMin: parseFloat(editValues.pvOnlyMin),
            pvOnlyCeiling: parseFloat(editValues.pvOnlyCeiling),
            zeroEscalatorCeiling: parseFloat(editValues.zeroEscalatorCeiling),
            absoluteCeiling: parseFloat(editValues.absoluteCeiling),
            escalationRateMin: parseFloat(editValues.escalationRateMin),
            escalationRateMax: parseFloat(editValues.escalationRateMax),
          }
        : {}),
      // EnergyBackup specific price sheet fields
      ...(isEnergyBackup && editValues.priceSheet
        ? {
            priceSheetId: editValues.priceSheet,
            escalationRateMin: parseFloat(editValues.escalationRateMin),
            escalationRateMax: parseFloat(editValues.escalationRateMax),
            minTerm: parseFloat(editValues.minTerm),
            maxTerm: parseFloat(editValues.maxTerm),
            minSystemCost: parseFloat(editValues.minSystemCost),
            maxSystemCost: parseFloat(editValues.maxSystemCost),
          }
        : {}),
      // Solar/NewHomes specific modifiers (not applicable to energyBackup or consumerSolar)
      ...(!isEnergyBackup && !isConsumerSolar
        ? {
            holdback: editValues.holdback,
            energyCommunity: editValues.energyCommunity,
            ppwModifier: editValues.ppwModifier,
            domesticContentModifier: editValues.domesticContentModifier,
          }
        : {}),
      constructionAdders: Object.fromEntries(
        Object.values(ConstructionAdderType)
          .map((type) => (editValues[type] ? [type, editValues[type]] : []))
          .filter(Boolean),
      ),
    };
    try {
      const result = await enqueueBulkJob({
        data: payload,
        programType: programType as ProgramType,
      }).unwrap();
      setEditValues({});
      setEditValuesDisplay({});
      setActiveJobId(result.jobId);
      toast.success(
        `Bulk update enqueued (${result.total} mappings). Progress will appear here — feel free to leave the page.`,
      );
    } catch (_e) {
      toast.error('Error enqueueing bulk price sheet mappings');
    }
  }, [editValues, enqueueBulkJob, advancedFilters, searchTerm, programType]);

  const columnConfig = useMemo(() => {
    const priceSheetOptions = Object.fromEntries(
      priceSheetResults?.data?.map((priceSheet) => [priceSheet.id, priceSheet.name]) ?? [],
    );

    if (programType === ProgramType.energyBackup) {
      return getEnergyBackupColumnConfig(editValuesDisplay, onColumnClick, priceSheetOptions);
    }

    if (programType === ProgramType.consumerSolar) {
      return getConsumerSolarColumnConfig(editValuesDisplay, onColumnClick, priceSheetOptions);
    }

    return getColumnConfig(editValuesDisplay, onColumnClick, priceSheetOptions);
  }, [editValuesDisplay, onColumnClick, priceSheetResults, programType]);

  return (
    <Card padding="sm">
      <Card.Header>
        <Box direction="row" justifyContent="space-between" alignItems="center">
          <Box gap="2xs">
            <Box fontSize="sm" color="body-primary">
              <Link to={`/admin/pricing/${programType}`}>Price Mappings</Link>
            </Box>
            <Box fontSize="2xl">Bulk Edit Active Price Mappings</Box>
          </Box>
          <Box fontSize="sm">
            <Link to={`/admin/pricing/${programType}/mappings/bulk-jobs`}>View Bulk Jobs</Link>
          </Box>
        </Box>
      </Card.Header>
      {currentEditColumn && (
        <BulkEditModal
          dataKey={currentEditColumn}
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          setEditValues={setEditValues}
          initialValues={editValues}
        />
      )}
      <FilteredMappingsList columnConfig={columnConfig} />
      <Box padding="sm" direction="row" justifyContent="space-between" alignItems="center">
        {isJobActive && jobStatus ? (
          <Box fontSize="sm" color="body-secondary">
            Bulk update in progress: {jobStatus.processed} / {jobStatus.total} ({jobStatus.status}). You can leave
            this page; it will continue.
          </Box>
        ) : (
          <Box />
        )}
        <Button isLoading={isLoading} onClick={confirmApplyBulkChanges} isDisabled={isJobActive}>
          {isJobActive ? 'Bulk update in progress…' : 'Apply Bulk Changes'}
        </Button>
      </Box>
    </Card>
  );
};
